import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { ProcessingType } from "..";

import { ENDPOINTS } from "@/api/application-api";
import { ToastType } from "@/enums";
import { useNavigateWithSearch } from "@/hooks";
import { getDraftDatasetInformation } from "@/queries";
import {
  useSaveDataset,
  useSetDraftDatasetStateFromDataset,
  useEditDatasetGroup,
  useDeleteDatasetGroup,
  useDeleteDataset,
} from "@/queries/datasets";
import { DatasetRead } from "@/types/datasets";
import { displayToast, displayErrorToast } from "@/utils/toast";

export const useActions = (
  workspaceId: string | undefined,
  datasets: DatasetRead[],
  datasetId: string | undefined,
  datasetGroupId: string | undefined,
  userToken: string | null,
  nonDraftVersionsExist: boolean,
  setOverwriteDraftDatasetModalOpen: (value: boolean) => void,
  setProcessingType: (type: ProcessingType) => void,
  folderId?: number | null,
  // eslint-disable-next-line max-params
) => {
  const queryClient = useQueryClient();
  const { mutateAsync: deleteDataset, isLoading: isDeletingDataset } = useDeleteDataset(userToken!);
  const { mutateAsync: deleteDatasetGroup, isLoading: isDeletingDatasetGroup } =
    useDeleteDatasetGroup(userToken!);
  const { mutateAsync: editDatasetGroup } = useEditDatasetGroup(userToken!, folderId);
  const navigateWithSearch = useNavigateWithSearch();
  const { mutateAsync: saveDataset, isLoading: isSavingDataset } = useSaveDataset(userToken!);
  const {
    mutateAsync: setDraftDatasetStateFromDataset,
    isLoading: isSettingDraftDatasetStateFromDataset,
  } = useSetDraftDatasetStateFromDataset(userToken!);

  const handleDeleteDatasetGroup = useCallback(async () => {
    try {
      const { draft_dataset_exists, draft_dataset_id } = await getDraftDatasetInformation(
        userToken!,
        datasetGroupId!,
      );

      if (draft_dataset_exists) {
        await deleteDataset(parseInt(draft_dataset_id!, 10));
      }

      deleteDatasetGroup(parseInt(datasetGroupId!, 10), {
        onSuccess: () => {
          if (folderId) {
            navigateWithSearch(`/workspace/${workspaceId}/folder/${folderId}`);
          } else {
            navigateWithSearch(`/workspace/${workspaceId}/home`);
          }
        },
        onError: () => {
          displayToast("Error deleting dataset group", ToastType.error);
        },
      });
    } catch {
      displayToast("Error deleting dataset group", ToastType.error);
    }
  }, [
    deleteDataset,
    deleteDatasetGroup,
    datasetGroupId,
    folderId,
    navigateWithSearch,
    userToken,
    workspaceId,
  ]);

  const handleDatasetGroupNameEdit = ({
    onSuccess,
    onError,
    newTitle,
  }: {
    onSuccess?: (newTitle: string) => void;
    onError?: () => void;
    newTitle: string;
  }) => {
    editDatasetGroup(
      {
        datasetGroupId: parseInt(datasetGroupId!, 10),
        name: newTitle,
      },
      {
        onSuccess: data => {
          if (onSuccess) {
            onSuccess(data.dataset_group.name);
          }
        },
        onError: () => {
          if (onError) {
            onError();
          }
        },
      },
    );
  };

  const handleDeleteClick = useCallback(async () => {
    if (nonDraftVersionsExist) {
      await deleteDataset(parseInt(datasetId!, 10));
      displayToast("Draft version deleted successfully", ToastType.success);
      const latestDatasetId = datasets.find(
        (dataset: DatasetRead) => dataset.version_number !== -1,
      )?.id;
      navigateWithSearch(
        `/workspace/${workspaceId}/dataset-groups/${datasetGroupId}/dataset/${latestDatasetId}`,
      );
    } else {
      await deleteDatasetGroup(parseInt(datasetGroupId!, 10));
      if (folderId) {
        navigateWithSearch(`/workspace/${workspaceId}/folder/${folderId}`);
      } else {
        navigateWithSearch(`/workspace/${workspaceId}/home`);
      }
      displayToast("Dataset deleted successfully", ToastType.success);
    }
  }, [
    datasetGroupId,
    datasetId,
    datasets,
    deleteDataset,
    deleteDatasetGroup,
    folderId,
    navigateWithSearch,
    nonDraftVersionsExist,
    workspaceId,
  ]);

  const handleEditClick = useCallback(async () => {
    const { draft_dataset_exists, is_draft_dataset_populated } = await getDraftDatasetInformation(
      userToken!,
      datasetGroupId!,
    );

    if (draft_dataset_exists && is_draft_dataset_populated) {
      setOverwriteDraftDatasetModalOpen(true);
      return;
    }

    // Set the processing type to newVersion
    setProcessingType("newVersion");

    try {
      await setDraftDatasetStateFromDataset({
        datasetId: parseInt(datasetId!, 10),
      });

      // The response now returns immediately with draft_dataset_id
      // The actual copying happens asynchronously via websocket
      // Navigation will happen in the websocket completion handler
    } catch {
      // Reset the processing type on error
      setProcessingType(null);
      displayErrorToast("Failed to create new version");
    }
  }, [
    datasetGroupId,
    datasetId,
    setDraftDatasetStateFromDataset,
    setOverwriteDraftDatasetModalOpen,
    userToken,
    setProcessingType,
  ]);

  const handleSaveAsNewVersionClick = useCallback(
    async (onSuccess?: () => void) => {
      try {
        setProcessingType("save");
        const response = await saveDataset({
          dataset_id: datasetId!,
          dataset_group_id: datasetGroupId!,
        });
        if (response.success) {
          if (onSuccess) {
            await queryClient.invalidateQueries({
              queryKey: [ENDPOINTS.dataset_groups],
            });
            await queryClient.invalidateQueries({
              queryKey: ["folder-entities", workspaceId, folderId ?? null],
            });

            onSuccess();
          } else {
            queryClient.invalidateQueries({
              queryKey: [ENDPOINTS.dataset_groups],
            });

            navigateWithSearch(
              `/workspace/${workspaceId}/dataset-groups/${datasetGroupId}/dataset/${datasetId}`,
            );
          }
        }
      } catch (error) {
        setProcessingType(null);
        displayErrorToast(error as string);
      }
    },
    [
      datasetGroupId,
      datasetId,
      navigateWithSearch,
      saveDataset,
      workspaceId,
      folderId,
      queryClient,
      setProcessingType,
    ],
  );

  return {
    handleDatasetGroupNameEdit,
    handleDeleteClick,
    handleEditClick,
    handleSaveAsNewVersionClick,
    isDeletingDataset,
    isDeletingDatasetGroup,
    isSavingDataset,
    isSettingDraftDatasetStateFromDataset,
    handleDeleteDatasetGroup,
  };
};
